import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Clock, Copy, Mail, MapPin, MessageCircle, Ticket } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';

const defaultImage = '/default-event.jpg';

const getEventImage = (event) => {
  const imageUrl = typeof event?.image_url === 'string' ? event.image_url.trim() : '';
  if (!imageUrl || imageUrl.includes('images.unsplash.com')) return defaultImage;
  return imageUrl;
};

const formatLongDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const EventDetails = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/events/${eventId}`);
        setEvent(response.data);
        document.title = `${response.data.title} | What's Happening Baltimore`;
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Unable to load this event right now.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  const buildShareData = () => {
    if (!event) return null;

    const venueLine = [event.venue_name, event.city].filter(Boolean).join(', ');
    const shareUrl = typeof window !== 'undefined' ? window.location.href : `https://whatshappeningbaltimore.com/events/${event.id}`;
    const shareText = [
      event.title,
      `${formatLongDate(event.event_date)}${event.event_time ? ` at ${event.event_time}` : ''}`,
      venueLine,
      event.description,
    ].filter(Boolean).join('\n\n');

    return {
      url: shareUrl,
      text: shareText,
      encodedUrl: encodeURIComponent(shareUrl),
      encodedTitle: encodeURIComponent(event.title),
      encodedText: encodeURIComponent(shareText),
    };
  };

  const handleCopyLink = async () => {
    const shareData = buildShareData();
    if (!shareData) return;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareData.url);
      alert('Event link copied to clipboard.');
    } else {
      window.prompt('Copy this event link:', shareData.url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-28 flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-black pt-28 px-6 flex items-center justify-center">
        <div className="max-w-lg text-center">
          <h1 className="font-outfit text-3xl font-bold text-white mb-3">Event not found</h1>
          <p className="text-zinc-400 mb-8">{error || 'This event may have been removed or is no longer available.'}</p>
          <Link to="/events" className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full font-semibold transition-colors">
            <ArrowLeft size={18} />
            Back to events
          </Link>
        </div>
      </div>
    );
  }

  const eventImage = getEventImage(event);
  const venueLine = [event.venue_name, event.city].filter(Boolean).join(', ');
  const fullAddress = [event.venue_address, event.city, event.state, event.zip_code].filter(Boolean).join(', ');
  const shareData = buildShareData();

  return (
    <div data-testid="event-details-page" className="min-h-screen bg-black pt-24">
      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-8">
          <Link to="/events" className="inline-flex items-center gap-2 text-zinc-400 hover:text-purple-400 transition-colors mb-8">
            <ArrowLeft size={18} />
            Back to all events
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start"
          >
            <div>
              <div className="aspect-video rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 shadow-2xl">
                <img src={eventImage} alt={event.title} className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="space-y-6">
              {event.is_featured && (
                <span className="inline-flex bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-semibold">Featured</span>
              )}
              <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight">{event.title}</h1>
              <p className="text-zinc-400 text-lg leading-relaxed">{event.description}</p>

              <div className="grid gap-4">
                <div className="flex items-start gap-3 text-zinc-300">
                  <Calendar className="text-purple-500 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-white">{formatLongDate(event.event_date)}</p>
                    {event.event_time && <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1"><Clock size={14} /> {event.event_time}</p>}
                  </div>
                </div>

                {(event.venue_name || fullAddress) && (
                  <div className="flex items-start gap-3 text-zinc-300">
                    <MapPin className="text-purple-500 mt-1" size={20} />
                    <div>
                      {event.venue_name && <p className="font-semibold text-white">{venueLine}</p>}
                      {fullAddress && <p className="text-sm text-zinc-400 mt-1">{fullAddress}</p>}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {event.ticket_url && (
                  <a
                    href={event.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                  >
                    <Ticket size={18} />
                    Get Tickets
                    <ArrowRight size={16} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setShowShareOptions((visible) => !visible)}
                  className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-purple-500 text-zinc-200 hover:text-purple-400 px-6 py-3 rounded-full font-semibold transition-colors"
                  aria-expanded={showShareOptions}
                >
                  <Copy size={18} />
                  Share Event
                </button>
              </div>

              {shareData && showShareOptions && (
                <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4 space-y-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Share this event</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${shareData.encodedUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900/80 px-3 py-3 text-zinc-200 hover:border-blue-500 hover:text-blue-400 transition-colors"
                      aria-label="Share on Facebook"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white font-bold">f</span>
                      <span className="text-xs font-semibold">Facebook</span>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${shareData.encodedUrl}&text=${shareData.encodedText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900/80 px-3 py-3 text-zinc-200 hover:border-white hover:text-white transition-colors"
                      aria-label="Share on X"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-black font-bold">X</span>
                      <span className="text-xs font-semibold">X</span>
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareData.encodedUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900/80 px-3 py-3 text-zinc-200 hover:border-sky-500 hover:text-sky-400 transition-colors"
                      aria-label="Share on LinkedIn"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-700 text-white font-bold">in</span>
                      <span className="text-xs font-semibold">LinkedIn</span>
                    </a>
                    <a
                      href={`mailto:?subject=${shareData.encodedTitle}&body=${shareData.encodedText}%0A%0A${shareData.encodedUrl}`}
                      className="flex flex-col items-center justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900/80 px-3 py-3 text-zinc-200 hover:border-purple-500 hover:text-purple-400 transition-colors"
                      aria-label="Share by email"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-purple-600 text-white"><Mail size={17} /></span>
                      <span className="text-xs font-semibold">Email</span>
                    </a>
                    <a
                      href={`sms:?&body=${shareData.encodedText}%0A%0A${shareData.encodedUrl}`}
                      className="flex flex-col items-center justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-900/80 px-3 py-3 text-zinc-200 hover:border-green-500 hover:text-green-400 transition-colors"
                      aria-label="Share by text message"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-600 text-white"><MessageCircle size={17} /></span>
                      <span className="text-xs font-semibold">Text</span>
                    </a>
                  </div>

                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-zinc-700 px-5 py-3 text-sm font-semibold text-zinc-200 hover:border-purple-500 hover:text-purple-400 transition-colors"
                  >
                    <Copy size={16} />
                    Copy Link
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EventDetails;
